import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import {
    configureMarketingTags,
    ensureDataLayer,
    scheduleMarketingTags,
} from '../utils/marketingTags';

interface UserData {
    em?: string;
    fn?: string;
    ln?: string;
    external_id?: string;
}

interface GTMContextType {
    trackEvent: (eventName: string, eventData?: Record<string, any>) => void;
    trackPageView: (pagePath: string, pageTitle?: string) => void;
    trackLead: (leadData?: Record<string, any>) => void;
    trackPurchase: (purchaseData: {
        transactionId: string;
        value: number;
        currency?: string;
        items?: any[];
        user_data?: UserData;
        [key: string]: any;
    }) => void;
    setUserId: (userId: string) => void;
    setUserProperties: (properties: Record<string, any>) => void;
}

const GTMContext = createContext<GTMContextType | undefined>(undefined);

interface GTMProviderProps {
    gtmId: string;
    children: ReactNode;
}

// Generate unique event ID for deduplication
const generateEventId = (prefix: string, transactionId?: string): string => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return transactionId
        ? `${prefix}_${transactionId}_${timestamp}`
        : `${prefix}_${timestamp}_${random}`;
};

export const GTMProvider: React.FC<GTMProviderProps> = ({ gtmId, children }) => {
    useEffect(() => {
        // dataLayer must exist before any funnel push; gtm.js starts on schedule (boot).
        ensureDataLayer();
        if (!gtmId) return;
        configureMarketingTags({ gtmId });
        return scheduleMarketingTags();
    }, [gtmId]);

    const trackEvent = (eventName: string, eventData?: Record<string, any>) => {
        ensureDataLayer();
        window.dataLayer.push({
            event: eventName,
            ...eventData,
        });
    };

    const trackPageView = (pagePath: string, pageTitle?: string) => {
        ensureDataLayer();
        window.dataLayer.push({
            event: 'pageview',
            page: {
                path: pagePath,
                title: pageTitle || document.title,
            },
        });
    };

    const trackLead = (leadData?: Record<string, any>) => {
        ensureDataLayer();
        window.dataLayer.push({
            event: 'lead',
            event_id: generateEventId('lead'),
            ...leadData,
        });
    };

    const trackPurchase = (purchaseData: {
        transactionId: string;
        value: number;
        currency?: string;
        items?: any[];
        user_data?: UserData;
        [key: string]: any;
    }) => {
        ensureDataLayer();

        // Unique event_id for Pixel + CAPI deduplication
        const eventId = generateEventId('purchase', purchaseData.transactionId);

        window.dataLayer.push({
            event: 'purchase',
            event_id: eventId,
            ecommerce: {
                transaction_id: purchaseData.transactionId,
                value: purchaseData.value,
                currency: purchaseData.currency || 'USD',
                items: purchaseData.items || [],
            },
            user_data: purchaseData.user_data
                ? {
                      em: purchaseData.user_data.em?.toLowerCase().trim(),
                      fn: purchaseData.user_data.fn?.toLowerCase().trim(),
                      ln: purchaseData.user_data.ln?.toLowerCase().trim(),
                      external_id: purchaseData.user_data.external_id,
                  }
                : undefined,
        });
    };

    const setUserId = (userId: string) => {
        ensureDataLayer();
        window.dataLayer.push({
            event: 'user_id_set',
            userId: userId,
        });
    };

    const setUserProperties = (properties: Record<string, any>) => {
        ensureDataLayer();
        window.dataLayer.push({
            event: 'user_properties_set',
            userProperties: properties,
        });
    };

    return (
        <GTMContext.Provider
            value={{
                trackEvent,
                trackPageView,
                trackLead,
                trackPurchase,
                setUserId,
                setUserProperties,
            }}
        >
            {children}
        </GTMContext.Provider>
    );
};

// Custom hook to use GTM
export const useGTM = (): GTMContextType => {
    const context = useContext(GTMContext);
    if (!context) {
        throw new Error('useGTM must be used within GTMProvider');
    }
    return context;
};
